import React from 'react';
import { useTranslation } from 'react-i18next';
import LoadingCard from '../../../../../components/loadingCard';
import RulesEmpty from './rulesEmpty';

import RatesManager, { generateClassNameRateName } from '../../../../rates/components/rateManager';
import { RateCategorySeat } from '../../../../rates/components/rateCategorySeat';
import { RateBundleInfo } from '../../../../rates/components/rateBundleInfo';
import { InheritedProperty } from '../../../../rates/ratesList';
import StyleRM from '../../../../../pages/rates/components/rateManager.module.css';

import { Table, Icon, Message } from '@weezevent/nacre';

function EventRow({ eventId, organization, events }) {
    const event = React.useMemo(() => events?.find(e => e.id === eventId), [events, eventId]);
    return event ? (
        <Table.Tr>
            <Table.Td>{event.name}</Table.Td>
            <Table.Td className="external-links">
                <div className="link">
                    <a href={`/ticket/O${organization?.id}/E${eventId}/settings/tickets_design/designs`} target="_blank" rel="noopener noreferrer">
                        <Icon name="external-link" />
                    </a>
                </div>
            </Table.Td>
        </Table.Tr>
    ) : null;
}

export default function ListEvents({ organization, design, events, eventsLoading }) {
    const { t } = useTranslation();

    const isDefault = React.useMemo(() => {
        if (!design || !design.rules) return false;
        return design.rules.some(r => r.organization_id === organization.id && r.event_id == null && r.rate_id == null);
    }, [organization, design]);

    const linkedEvents = React.useMemo(() => {
        if (!design || !design.rules) return [];
        return design.rules.reduce((allRules, rule) => {
            if (rule.event_id !== null && !rule.rate_id && !allRules.includes(rule.event_id)) allRules.push(rule.event_id);
            return allRules;
        }, []);
    }, [design]);

    if (!design) return null;
    if (isDefault)
        return (
            <Message type="default" title={t('ticket.ticket_design.designs.default.title')}>
                {t('ticket.ticket_design.designs.default.description')}
            </Message>
        );
    return (
        <>
            <Table useNew>
                <Table.THead>
                    <Table.Tr>
                        <th>{t('ticket.export.headers.event_name')}</th>
                        <th />
                    </Table.Tr>
                </Table.THead>
                {!eventsLoading && linkedEvents && (
                    <Table.TBody>
                        {linkedEvents.map((eventId, key) => (
                            <EventRow key={key} eventId={eventId} organization={organization} events={events} />
                        ))}
                    </Table.TBody>
                )}
            </Table>
            <LoadingCard loading={eventsLoading} />
            {!eventsLoading && !linkedEvents?.length && <RulesEmpty designId={design.id} />}
        </>
    );
}

function RatesTable({ organization, linkedRates, t, event }) {
    let tableCells = React.useMemo(() => {
        return [
            {
                header: t('ticket.rates.rateName'),
                renderRate: (rate, { categories, groups }) => (
                    <InheritedProperty
                        displayIcon={false}
                        property="name"
                        object={rate}
                        handleProperty={name => (
                            <div className={StyleRM[generateClassNameRateName(rate, groups)]} style={{ display: 'flex', alignItems: 'center' }}>
                                <RateCategorySeat event={event} rate={rate} categories={categories} />
                                {name}
                                <RateBundleInfo bundleSize={rate.bundle_size || 1} />
                            </div>
                        )}
                        template="rate_template"
                    />
                ),
                renderGroup: (group, onToggle) => {
                    return (
                        <div className={StyleRM['rates-table__cell-group']} onClick={() => onToggle(group)}>
                            {group.parent && <Icon className={StyleRM['rates-table__cell-group-icon-subgroup']} name="link-group" />}
                            <Icon name={group.showRates ? 'folder-open' : 'folder-closed'} style={{ marginRight: '5px' }} />
                            <InheritedProperty displayIcon={false} property="name" object={group} handleProperty={name => <b>{name}</b>} template="group_template" />
                        </div>
                    );
                }
            }
        ];
    }, [event]);
    return (
        event && (
            <RatesManager
                event={event}
                organization={organization}
                queryParams={{ id_in: linkedRates }}
                initialQuery={{
                    organizationId: organization.id,
                    eventId: event.id
                }}
                tableCells={tableCells}
                mode={'other'}
                hideEmptyGroup={false}
                selectable={false}
                activeSyncRates={false}
                activeCacheRates
                activeCacheGroups
            />
        )
    );
}

export function ListRates({ organization, design, event }) {
    const { t } = useTranslation();

    const linkedRates = React.useMemo(() => {
        if (!design || !design.rules) return [];
        return design.rules.reduce((allRules, rule) => {
            if (rule.rate_id) {
                allRules.push(rule.rate_id);
            }
            return allRules;
        }, []);
    }, [design]);

    const isDefault = React.useMemo(() => {
        return design.rules.some(r => r.organization_id === organization?.id && r.event_id === event?.id && r.rate_id === null);
    }, [design, organization, event]);

    if (!design) return null;
    if (isDefault)
        return (
            <Message type="default" title={t('ticket.ticket_design.designs.default.title')}>
                {t('ticket.ticket_design.designs.default.description_event')}
            </Message>
        );
    return (
        <>
            {linkedRates.length > 0 && <RatesTable t={t} event={event} linkedRates={linkedRates} organization={organization} />}
            {!linkedRates.length && <RulesEmpty designId={design?.id} event={event} />}
        </>
    );
}
